import { useCallback } from "react";

import { EventContentArg } from "@fullcalendar/common";
import cn from "classnames";
import camelCase from "lodash-es/camelCase";

import { HexColor } from "@mapmycustomers/shared/types/AnyColor";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import anyColorToHex from "@mapmycustomers/shared/util/color/anyColorToHex";

import CustomActivityTypeColor, { colorToName } from "@app/enum/CustomActivityTypeColor";
import EventObject from "@app/scene/activity/component/ActivityCalendarPage/type/EventObject";
import isFutureActivity from "@app/util/activity/isFutureActivity";
import getClosestColor from "@app/util/colors/getClosestColor";

import styles from "./useEventClassNamesGetter.module.scss";

const getDefaultActivityTypeClassName = (
  activityTypeName: ActivityType["name"],
  past?: boolean
) => {
  return styles[`${activityTypeName.toLowerCase()}${past ? "__past" : ""}`];
};

const getCustomActivityTypeClassName = (activityTypeColor: HexColor, overdue?: boolean) => {
  if (activityTypeColor && colorToName[activityTypeColor]) {
    return styles[`custom_${camelCase(colorToName[activityTypeColor])}${overdue ? "__past" : ""}`];
  } else {
    return styles[`default${overdue ? "__past" : ""}`];
  }
};

const useEventClassNamesGetter = (activityTypes: ActivityType[]) => {
  const defaultActivityTypeIds = activityTypes
    .filter((activityType) => activityType.isDefault)
    .map(({ id }) => id);

  return useCallback(
    (args: EventContentArg): string => {
      const payload = args.event.extendedProps as EventObject;
      if (!payload) {
        return "";
      }
      const classes = [styles.container];
      if (payload.activity) {
        const isDefaultActivityType = defaultActivityTypeIds.includes(
          payload.activity.crmActivityType.id
        );
        const isPast = !isFutureActivity(payload.activity);

        if (payload.activity.crmActivityType) {
          if (isDefaultActivityType) {
            classes.push(
              getDefaultActivityTypeClassName(payload.activity.crmActivityType.name, isPast)
            );
          } else {
            let activityTypeColor = payload.activity.crmActivityType.color ?? undefined;
            if (activityTypeColor) {
              activityTypeColor = getClosestColor(
                Object.values(CustomActivityTypeColor),
                activityTypeColor,
                activityTypeColor,
                50
              );
              classes.push(
                getCustomActivityTypeClassName(anyColorToHex(activityTypeColor), isPast)
              );
            }
          }
        }

        if (isPast) {
          classes.push(styles.past);
        }
      }

      return cn(classes);
    },
    [defaultActivityTypeIds]
  );
};

export default useEventClassNamesGetter;
